import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";
import { useBreadcrumbs, useTabNavigate } from "@/app-shell";
import { ReportCurrencyProvider } from "@/profitability/currency";
import { ReportInputsProvider } from "@/profitability/missing-value";
import { RecordPanel } from "@/profitability/records";
import { recordLabel } from "@/profitability/records/objects";
import { RecordSheet } from "@/profitability/records/record-sheet";
import { useTemplateRecords } from "@/profitability/records/use-template-objects";
import { ReviewSpecProvider } from "@/profitability/review/review-spec";
import {
  EDIT_PARAM,
  PROFITABILITY_PATH,
  profitabilityRunPath,
  profitabilityTemplatePanePath,
  profitabilityTemplatePath,
} from "@/profitability/routes";
import { SourceProvider } from "@/profitability/sources";
import { PublishDraft } from "@/profitability/template-chat/workspace/publish-draft";
import { runDates } from "@/profitability/utils";
import { AgentBuilder } from "./agent-builder";
import { AgentPagePending } from "./agent-page-skeleton";
import { EditAgentAside } from "./edit-agent-aside";
import { PeriodHistorySheet } from "./period-history-sheet";
import { DraftChanges } from "./review-changes";
import { TemplateHeader } from "./template-header";
import { TemplateBuildNotice, TemplateLoadNotice } from "./template-notices";
import { TemplateSections } from "./template-sections";
import { TemplateTabBar } from "./template-tab-bar";
import { currentTab, paneOf, paneTab, templatePageTabs } from "./template-tabs";
import { useBuildHistoryRefresh } from "./use-build-history-refresh";
import { useEditAgent } from "./use-edit-agent";
import { usePageEdge } from "./use-page-edge";
import { useForgetRemovedPeriod, usePeriodParams } from "./use-period-params";
import { useTemplatePeriod } from "./use-template-period";
import { useTemplateReport } from "./use-template-report";
import { VolcaChatAside } from "./volca-chat-aside";

/**
 * An agent's page. Before it is published, for whoever builds it, it is the
 * builder: the sample week Volca built, Edit agent beside it, Publish in the
 * header. Once published, every page is one period. The header names it and
 * the control beside the title moves between periods. Review keeps the period in
 * the findings column, and the page is the finding.
 * `?period=` names it (the latest when absent) and `?compare=` what it is
 * compared with; both ride along from tab to tab. A draft of changes waits
 * under the header to be reviewed and published.
 */
export const TemplatePage = ({
  businessId,
  reportId,
}: {
  businessId: string;
  reportId: string;
}) => {
  const navigate = useNavigate();
  const templatePath = profitabilityTemplatePath(reportId);
  const isMobile = useIsMobile();
  const tabNavigate = useTabNavigate();
  const { section, pane } = useParams();
  const [search] = useSearchParams();
  const { report, query: reports } = useTemplateReport(
    businessId,
    reportId,
    search.get(EDIT_PARAM) === "1",
  );
  const params = usePeriodParams();
  const { periodId, compare } = params;
  const period = useTemplatePeriod(businessId, reportId, periodId, compare);
  useForgetRemovedPeriod(period.overview.error, params);
  const { run } = period;
  const { objects, spec, output, currency, recordsPending } =
    useTemplateRecords(businessId, reportId, periodId);
  const { review, panes, tabs } = templatePageTabs({
    output,
    work: spec.work,
    documents: !!run,
    activeCount: period.activeCount,
  });
  const tab = currentTab({
    section,
    pane,
    tabs,
    review,
    waiting: period.overview.isPending,
  });
  const edge = usePageEdge(tab === "review", period.requestedId);
  const { manages, settingUp, editing, nav, session } = useEditAgent(
    businessId,
    reportId,
    report,
    edge,
    run?.id ?? null,
  );
  // Every period and rerun, over the page.
  const [history, setHistory] = useState(false);
  const [reviewingChanges, setReviewingChanges] = useState(false);
  // A record tab is one long table, the workbook a grid, the documents a
  // file browser, and Review the record itself: they fill the page.
  const fills =
    (!settingUp && (tab === "workbook" || tab === "review")) ||
    tab === "documents" ||
    panes.some(
      (item) => paneTab(item.id) === tab && item.surface === "records",
    );
  useBuildHistoryRefresh(businessId, report?.build_status);
  useBreadcrumbs(
    [
      { label: "Job Costing", href: PROFITABILITY_PATH },
      { label: report?.name ?? "Agent" },
    ],
    // The column already names the product and the agent.
    { visible: false },
  );
  const goTo = (value: string) => {
    const declared = paneOf(value);
    const path = declared
      ? profitabilityTemplatePanePath(reportId, declared)
      : `${templatePath}/${value}`;
    tabNavigate(`${path}${params.search()}`);
  };
  // The latest period is the page's default, so going to it clears the
  // choice and the page follows whatever is latest.
  const latestId = period.overview.data?.latest_run_id ?? null;
  const showPeriod = (runId: string | null) => {
    edge.clearEdge();
    params.setPeriod(runId && runId === latestId ? null : runId);
  };
  // Closing the sheet, or opening a period from it.
  const leaveHistory = (runId?: string) => {
    setHistory(false);
    if (runId) showPeriod(runId);
  };
  if (!report && reports.isPending) return <AgentPagePending />;

  return (
    <ReportCurrencyProvider currency={currency}>
      <ReportInputsProvider inputs={report?.inputs}>
        <ReviewSpecProvider spec={spec}>
          <SourceProvider
            businessId={businessId}
            reportId={reportId}
            runId={run?.id ?? null}
            compare={compare}
            dates={run ? runDates(run) : null}
            canUpload={manages && !!report?.published}
          >
            <PageContainer
              variant={fills ? "workspace" : "document"}
              className={cn({ "pb-0 md:px-4": tab === "review" })}
            >
              <TemplateLoadNotice reports={reports} found={!!report} />
              {report && (
                <>
                  <TemplateHeader
                    businessId={businessId}
                    report={report}
                    period={period}
                    objects={objects}
                    settings={output?.settings}
                    compare={compare}
                    onCompareChange={params.setCompare}
                    onPeriodChange={showPeriod}
                    onAllPeriods={() => setHistory(true)}
                    onAsk={edge.ask}
                    publish={
                      settingUp && (
                        <PublishDraft
                          businessId={businessId}
                          report={report}
                          answering={session.answering}
                          quiet
                          onPublished={() => navigate(templatePath)}
                        />
                      )
                    }
                  />
                  <TemplateBuildNotice
                    businessId={businessId}
                    report={report}
                  />
                  {manages && (
                    <DraftChanges
                      businessId={businessId}
                      report={report}
                      answering={session.answering}
                      open={reviewingChanges}
                      onOpenChange={setReviewingChanges}
                    />
                  )}
                  {settingUp ? (
                    <AgentBuilder
                      businessId={businessId}
                      report={report}
                      editing={editing}
                      onEdit={() => nav.edit()}
                    />
                  ) : (
                    <>
                      {/* On desktop the nav column lists the tabs. */}
                      {isMobile && (
                        <TemplateTabBar
                          tabs={tabs}
                          value={tab}
                          onChange={goTo}
                        />
                      )}
                      <TemplateSections
                        businessId={businessId}
                        report={report}
                        tab={tab}
                        period={period}
                        periodId={periodId}
                        compare={compare}
                        output={output}
                        objects={objects}
                        panes={panes}
                        review={review}
                        edge={edge}
                        onPaneChange={(id) => goTo(`pane/${id}`)}
                        onLatest={() => showPeriod(null)}
                        recordsPending={recordsPending}
                      />
                    </>
                  )}
                  <PeriodHistorySheet
                    open={history}
                    onOpenChange={(open) =>
                      open ? setHistory(true) : leaveHistory()
                    }
                    businessId={businessId}
                    reportId={report.id}
                    onOpenRun={(runId, row) =>
                      // A period's result opens here; a run still building, or
                      // one that failed, on its own page.
                      row.status === "completed" && row.workbook_available
                        ? leaveHistory(runId)
                        : navigate(profitabilityRunPath(runId))
                    }
                  />
                </>
              )}
            </PageContainer>
            <RecordSheet
              open={!!edge.record && tab !== "review"}
              onClose={edge.closeRecord}
              returnFocus={edge.record?.list?.returnFocus}
              label={
                edge.record ? recordLabel(objects, edge.record.ref) : "Record"
              }
            >
              {edge.record && report && (
                <RecordPanel
                  key={JSON.stringify(edge.record.ref)}
                  businessId={businessId}
                  reportId={report.id}
                  objects={objects}
                  selected={edge.record.ref}
                  list={edge.record.list}
                  onOpenRecord={edge.openRecord}
                  onClose={edge.closeRecord}
                  period={run && { dates: runDates(run), runId: run.id }}
                  canUpload={manages && report.published}
                  onAsk={run ? edge.ask : undefined}
                />
              )}
            </RecordSheet>
            {report && manages && (
              <EditAgentAside
                businessId={businessId}
                report={report}
                session={session}
                open={editing}
                onClose={nav.closeEdit}
                // Before publishing, the sample week is already the page.
                onReview={
                  report.published ? () => setReviewingChanges(true) : undefined
                }
              />
            )}
            {run && (
              <VolcaChatAside
                objects={objects}
                figures={run.summary.map((figure) => figure.label)}
                businessId={businessId}
                runId={edge.chat?.runId ?? run.id}
                open={!!edge.chat && !editing}
                question={edge.chat?.question ?? null}
                onQuestionPrepared={edge.questionPrepared}
                selection={edge.selection}
                onClearSelection={edge.clearSelection}
                onClose={edge.closeChat}
              />
            )}
          </SourceProvider>
        </ReviewSpecProvider>
      </ReportInputsProvider>
    </ReportCurrencyProvider>
  );
};
